/**
 * WebSocket Manager - Thin wrapper over SocketService.
 * CONNECTED only after server connectionSuccess. Single socket; listener-first; join queue.
 * See FRONTEND_WEBSOCKET_COMPLETE_INTEGRATION_GUIDE and socket/SocketService.js.
 */

import { getSocketServiceInstance } from '@/shared/services/socket/SocketService';
import { ConnectionState } from '@/shared/services/socket/constants';
import { liveSocketKeepAlive } from '@/domains/live/services/liveSocketKeepAlive';

const ACK_TIMEOUT_MS = 10000;

function getCore() {
  return getSocketServiceInstance();
}

export { ConnectionState };

/**
 * @returns {ReturnType<getSocketServiceInstance>}
 */
export default new Proxy(
  {},
  {
    get(_, prop) {
      const c = getCore();
      if (prop === 'connect') return () => c.connect();
      if (prop === 'disconnect') {
        return () => {
          if (liveSocketKeepAlive.hasActiveLiveSession) return;
          c.disconnect();
        };
      }
      if (prop === 'getState') return () => c.getState();
      if (prop === 'isConnected') return () => c.isConnected();
      if (prop === 'onStateChange') return (cb) => c.onStateChange(cb);
      if (prop === 'offStateChange') return (cb) => c.offStateChange(cb);
      if (prop === 'on') return (event, cb) => c.on(event, cb);
      if (prop === 'off') return (event, cb) => c.off(event, cb);
      if (prop === 'requestJoinConversation') return (conversationId) => c.requestJoinConversation(conversationId);
      if (prop === 'leaveConversation') return (conversationId) => c.leaveConversation(conversationId);
      if (prop === 'getSocket') return () => c.getSocket();
      if (prop === 'emit') {
        return (event, data, options = {}) => {
          if (event === 'joinConversation' && data?.conversationId) {
            c.requestJoinConversation(data.conversationId);
            return Promise.resolve({ success: true });
          }
          if (event === 'leaveConversation' && data?.conversationId) {
            c.leaveConversation(data.conversationId);
            return Promise.resolve({ success: true });
          }
          const { timeout = ACK_TIMEOUT_MS, silentIfOffline = false } = options;
          // silentIfOffline: when true, resolve without sending (legacy no-op; no queue exists)
          if (!c.isConnected()) {
            if (silentIfOffline) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('Socket not connected'));
          }
          const socket = c.getSocket();
          if (!socket) return Promise.reject(new Error('No socket'));
          return new Promise((resolve, reject) => {
            socket.timeout(timeout).emit(event, data, (err, response) => {
              if (err) reject(err);
              else resolve(response);
            });
          });
        };
      }
      return c[prop];
    },
  }
);
